import {computed, DOCUMENT, inject, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';

/** Le BFF évalue lui-même le rôle d'administration : le front ne connaît pas son nom. */
export interface BffUser {
  isAuthenticated: boolean;
  name: string | null;
  isAdmin: boolean;
}

const ANONYMOUS: BffUser = {isAuthenticated: false, name: null, isAdmin: false};

/**
 * Session portée par le BFF (cookie HttpOnly) : le navigateur ne manipule aucun token.
 */
@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);

  private readonly currentUser = signal<BffUser | null>(null);

  readonly user = computed(() => this.currentUser() ?? ANONYMOUS);
  readonly isAdmin = computed(() => this.user().isAdmin);

  /** Charge (une fois) l'utilisateur courant depuis le BFF. */
  async load(force = false): Promise<BffUser> {
    const cached = this.currentUser();
    if (cached && !force) {
      return cached;
    }

    try {
      const user = await firstValueFrom(this.http.get<BffUser>('/bff/user'));
      this.currentUser.set(user);
      return user;
    } catch {
      this.currentUser.set(ANONYMOUS);
      return ANONYMOUS;
    }
  }

  login(returnUrl: string = this.document.location.pathname): void {
    this.document.location.href = `/bff/login?returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  logout(): void {
    this.document.location.href = '/bff/logout';
  }
}
